// video-suite/main.js — 视频工作台装配入口

import { bindUserDropdown, ensureAuthOrRedirect } from './auth.js';
import { els, setStatus } from './dom.js';
import { convertFiles } from './media-files.js';
import { state } from './state.js';
import {
  imageLimit,
  renderBoundListing,
  renderCostEstimate,
  renderPromptSource,
  renderPreviewNotice,
  renderReferenceImages,
  renderTypeOptions,
  syncModelControls,
} from './view.js';
import { createListingController } from './listing-controller.js';
import { loadListingImagePicker, renderListingImagePicker } from './listing-image-picker.js';
import { createVideoTaskController } from './video-task.js';
import { getVideoSubmitBlockReason } from './readiness.js';
import { renderAppVersionBadge } from '../../app-version-badge.js';
import { parseProjectFromUrl } from '../shared/project-bind.js';
import { request as httpRequest } from '../../interaction/transport/http-client.js';

function refreshSubmitState() {
  const btn = els.generateBtn();
  if (!btn) return;
  const blockReason = getVideoSubmitBlockReason(state);
  btn.disabled = Boolean(state.activeTaskId);
  btn.dataset.blocked = blockReason && !state.activeTaskId ? '1' : '0';
  btn.setAttribute('aria-disabled', blockReason ? 'true' : 'false');
  btn.title = blockReason || '';
  renderCostEstimate(state);
  renderPromptSource(state);
}

function markPreviewStale(message = '参数已变更，请重新生成视频。') {
  if (state.activeTaskId) return;
  state.previewStale = true;
  renderPreviewNotice(message);
}

function refreshAndMark(message) {
  refreshSubmitState();
  markPreviewStale(message);
}

function bindReferenceImageInput() {
  els.referenceInput()?.addEventListener('change', async (ev) => {
    try {
      const next = await convertFiles([...ev.target.files]);
      state.referenceSource = next.length ? 'upload' : '';
      state.referenceImages = next.slice(0, imageLimit(state));
      renderReferenceImages(state);
      renderPromptSource(state);
      renderListingImagePicker(state, { refreshSubmitState, markPreviewStale });
      refreshAndMark('参考图已更新，请重新生成视频。');
      setStatus('');
    } catch (err) {
      setStatus(err?.message || '读取参考图失败', 'error');
    }
  });
}

function bindLastFrameInput() {
  els.lastFrameInput()?.addEventListener('change', async (ev) => {
    try {
      const [file] = [...ev.target.files];
      if (!file) return;
      const [next] = await convertFiles([file]);
      state.lastFrame = next || null;
      const label = els.lastFrameLabel();
      if (label) label.textContent = state.lastFrame ? `已选择：${state.lastFrame.name}` : '上传尾帧会关闭声音';
      refreshAndMark('尾帧已更新，请重新生成视频。');
    } catch (err) {
      setStatus(err?.message || '读取尾帧失败', 'error');
    }
  });
}

function bindFormEvents(videoTasks) {
  const syncControls = () => {
      syncModelControls(state, {
        refreshSubmitState,
        onModeChange: () => markPreviewStale('生成模式已切换，请重新生成视频。'),
      });
      renderListingImagePicker(state, { refreshSubmitState, markPreviewStale });
      markPreviewStale('视频模型参数已更新，请重新生成视频。');
    };
  els.videoType()?.addEventListener('change', syncControls);
  els.videoTime()?.addEventListener('change', () => refreshAndMark('视频时长已更新，请重新生成视频。'));
  els.resolution()?.addEventListener('change', () => refreshAndMark('视频分辨率已更新，请重新生成视频。'));
  els.ratio()?.addEventListener('change', () => refreshAndMark('视频比例已更新，请重新生成视频。'));
  els.prompt()?.addEventListener('input', () => {
    state.promptSource = 'manual';
    refreshAndMark('视频创意描述已更新，请重新生成视频。');
  });
  els.generateBtn()?.addEventListener('click', videoTasks.submitVideo);
  bindReferenceImageInput();
  bindLastFrameInput();
}

// ── 项目绑定 ────────────────────────────────────────────────────
// 项目绑定通过 Listing 区域内的下拉框完成（先选项目再选 Listing）。
let listingController = null;

async function initProjectSelect() {
  const select = document.getElementById('video-project-select');
  const listingQuery = document.getElementById('video-listing-query');
  if (!select) return;

  // 加载项目列表并填充下拉框
  try {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('pageSize', '50');
    const result = await httpRequest(`/api/new-product-projects?${params.toString()}`);
    const items = result?.items || [];
    items.forEach((it) => {
      const opt = document.createElement('option');
      opt.value = String(it.id);
      opt.textContent = `${it.projectNo || `#${it.id}`} · ${it.siteCode || ''} · ${it.asin || ''}`;
      select.appendChild(opt);
    });
  } catch (err) {
    setStatus('项目列表加载失败', 'error');
  }

  // URL 自动绑定项目；默认无项目（空选项）
  const initialProjectId = parseProjectFromUrl();
  let projectSelectLocked = false;
  if (initialProjectId) {
    if (![...select.options].some((o) => o.value === String(initialProjectId))) {
      const opt = document.createElement('option');
      opt.value = String(initialProjectId);
      opt.textContent = `项目 #${initialProjectId}（URL 继承）`;
      select.appendChild(opt);
    }
    select.value = String(initialProjectId);
    state.newProductProjectId = initialProjectId;
    // Upstream fromProject is forced inheritance — prevent silent rebind.
    select.disabled = true;
    projectSelectLocked = true;
  } else {
    select.value = '';
    state.newProductProjectId = null;
  }
  if (listingQuery) listingQuery.disabled = false;

  // 项目选择变更（空值 = 无项目）
  select.addEventListener('change', () => {
    if (projectSelectLocked) return;
    const pid = Number(select.value) || null;
    state.newProductProjectId = pid;
    // 切换项目时清空 Listing 绑定
    state.listing = null;
    renderBoundListing(null);
    refreshSubmitState();
    if (listingQuery) listingQuery.disabled = false;
    if (listingController) {
      listingController.searchListings();
    } else {
      const host = document.getElementById('video-listing-results');
      if (host) host.replaceChildren();
    }
  });
}

async function bootstrap() {
  bindUserDropdown();
  const ok = await ensureAuthOrRedirect();
  if (!ok) return;
  renderAppVersionBadge();

  const videoTasks = createVideoTaskController(state, { refreshSubmitState });
  listingController = createListingController(state, {
    refreshSubmitState,
    loadVideos: videoTasks.loadVideos,
    loadListingImages: () => loadListingImagePicker(state, { refreshSubmitState, markPreviewStale }),
  });
  const listing = listingController;

  renderTypeOptions();
  bindFormEvents(videoTasks);
  listing.bindSearchInput();
  syncModelControls(state, {
    refreshSubmitState,
    onModeChange: () => markPreviewStale('生成模式已切换，请重新生成视频。'),
  });
  renderPromptSource(state);
  window.addEventListener('beforeunload', videoTasks.stopPolling);
  // 初始化项目下拉框（先选项目再选 Listing）
  await initProjectSelect();
  // 项目就绪后加载 Listing（如 URL 带了 fromListing 参数）
  await listing.loadIntentListing();
}

bootstrap();
